import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { getAuthHeader } from '../utils/auth';
import {
  Phone, MessageCircle, Plus, Search, X,
  Flame, Star, Minus, Ban, Clock, CheckCircle2, AlertTriangle,
  Users, PhoneCall, TrendingUp, RefreshCw, Upload, Download, FileText,
  List, Columns, ChevronDown, Settings,
  DollarSign, Check, Trash2, Info
} from 'lucide-react';

// ── constants ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  new:       { label: 'New',      bg: 'bg-blue-500/20',    text: 'text-blue-400',    border: 'border-blue-500/30',    kanbanBg: 'bg-blue-500/10'   },
  called:    { label: 'Called',   bg: 'bg-yellow-500/20',  text: 'text-yellow-400',  border: 'border-yellow-500/30',  kanbanBg: 'bg-yellow-500/10' },
  follow_up: { label: 'Callback', bg: 'bg-orange-500/20',  text: 'text-orange-400',  border: 'border-orange-500/30',  kanbanBg: 'bg-orange-500/10' },
  converted: { label: 'Won',      bg: 'bg-[#22c55e]/20',   text: 'text-[#22c55e]',   border: 'border-[#22c55e]/30',   kanbanBg: 'bg-[#22c55e]/10'  },
  lost:      { label: 'Lost',     bg: 'bg-red-500/20',     text: 'text-red-400',     border: 'border-red-500/30',     kanbanBg: 'bg-red-500/10'    },
};

const RATING_CONFIG = {
  high:   { icon: Flame,  color: 'text-orange-400', label: 'Hot'  },
  medium: { icon: Star,   color: 'text-amber-400',  label: 'Warm' },
  low:    { icon: Minus,  color: 'text-blue-400',   label: 'Cold' },
  spam:   { icon: Ban,    color: 'text-red-400',    label: 'Spam' },
};

const CALL_OUTCOMES = [
  { id: 'connected',      label: 'Connected',       color: 'text-[#22c55e]',  bg: 'bg-[#22c55e]/10',  border: 'border-[#22c55e]/30'  },
  { id: 'no_answer',      label: 'No Answer',        color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  { id: 'voicemail',      label: 'Voicemail',        color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30'   },
  { id: 'callback',       label: 'Callback Later',   color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  { id: 'not_interested', label: 'Not Interested',   color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/30'    },
  { id: 'converted',      label: 'Converted / Won',  color: 'text-[#22c55e]',  bg: 'bg-[#22c55e]/10',  border: 'border-[#22c55e]/30'  },
];

const SOURCE_CONFIG = {
  manual:      { label: 'Manual',      bg: 'bg-zinc-700/40',    text: 'text-zinc-400'   },
  meta_ads:    { label: 'Meta Ads',    bg: 'bg-blue-500/20',    text: 'text-blue-400'   },
  bulk_upload: { label: 'CSV Import',  bg: 'bg-indigo-500/20',  text: 'text-indigo-400' },
  meta:        { label: 'Meta',        bg: 'bg-blue-500/20',    text: 'text-blue-400'   },
  google:      { label: 'Google',      bg: 'bg-cyan-500/20',    text: 'text-cyan-400'   },
  referral:    { label: 'Referral',    bg: 'bg-amber-500/20',   text: 'text-amber-400'  },
  walk_in:     { label: 'Walk-in',     bg: 'bg-[#22c55e]/20',   text: 'text-[#22c55e]'  },
};

const LOST_SUBSTATUS = [
  { value: 'rnr',            label: 'RNR',             desc: 'Ring No Response',      colorClass: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
  { value: 'trash',          label: 'Trash',           desc: 'Spam / Wrong number',   colorClass: 'text-red-400 border-red-500/30 bg-red-500/10' },
  { value: 'just_enquired',  label: 'Just Enquired',   desc: 'No buying intent',      colorClass: 'text-zinc-400 border-white/10 bg-white/5' },
  { value: 'not_interested', label: 'Not Interested',  desc: 'Declined offer',        colorClass: 'text-zinc-400 border-white/10 bg-white/5' },
  { value: 'budget_issue',   label: 'Budget Issue',    desc: "Can't afford",          colorClass: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
  { value: 'competitor',     label: 'Competitor',      desc: 'Chose another provider',colorClass: 'text-red-400 border-red-500/30 bg-red-500/10' },
];

const SUB_STATUS_CONFIG = {
  rnr:            { label: 'RNR',           colorClass: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  trash:          { label: 'Trash',         colorClass: 'text-red-400 bg-red-500/10 border-red-500/20' },
  just_enquired:  { label: 'Just Enquired', colorClass: 'text-zinc-400 bg-white/5 border-white/10' },
  not_interested: { label: 'Not Interested',colorClass: 'text-zinc-400 bg-white/5 border-white/10' },
  budget_issue:   { label: 'Budget Issue',  colorClass: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  competitor:     { label: 'Competitor',    colorClass: 'text-red-400 bg-red-500/10 border-red-500/20' },
};

// ── helpers ───────────────────────────────────────────────────────────────────

const SourceBadge = ({ source }) => {
  const cfg = SOURCE_CONFIG[source] || SOURCE_CONFIG.manual;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

const SubStatusBadge = ({ subStatus }) => {
  if (!subStatus) return null;
  const cfg = SUB_STATUS_CONFIG[subStatus];
  if (!cfg) return null;
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${cfg.colorClass}`}>
      {cfg.label}
    </span>
  );
};

const formatCurrency = v => {
  if (!v) return null;
  if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v/1000).toFixed(0)}K`;
  return `₹${v}`;
};

const formatCurrencyFull = v => {
  if (!v && v !== 0) return '₹0';
  return `₹${Number(v).toLocaleString('en-IN')}`;
};

const timeAgo = (iso) => {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff/60)}m ago`;
  if (diff < 86400) return `${Math.round(diff/3600)}h ago`;
  return `${Math.round(diff/86400)}d ago`;
};

const SLABadge = ({ sla }) => {
  if (!sla) return null;
  const colors = {
    critical: 'text-red-400 bg-red-500/10 border-red-500/20',
    warning: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
    info: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono border ${colors[sla.severity]}`}>
      <Clock className="w-2.5 h-2.5" />{sla.label}
    </span>
  );
};

const getSmartHint = (lead) => {
  if (lead.status === 'new' && lead.sla_status?.severity === 'critical')
    return { text: 'Call immediately — lead is over 1 hour old', color: 'red', Icon: AlertTriangle };
  if (lead.status === 'follow_up' && lead.follow_up_at && new Date(lead.follow_up_at) < new Date())
    return { text: 'Follow-up is overdue', color: 'amber', Icon: Clock };
  if (lead.call_count === 0)
    return { text: 'First contact — introduce yourself and understand their need', color: 'indigo', Icon: Info };
  if (lead.call_count >= 3 && lead.status !== 'converted' && lead.status !== 'lost')
    return { text: `${lead.call_count} calls made — consider marking as RNR or Lost`, color: 'amber', Icon: AlertTriangle };
  return null;
};

const HINT_COLORS = {
  red: 'bg-red-500/10 border-red-500/20 text-red-400',
  amber: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  indigo: 'bg-[#6366f1]/10 border-[#6366f1]/20 text-[#6366f1]',
};

// ── Service Manager ───────────────────────────────────────────────────────────

const ServiceManager = ({ services, onClose, onRefresh }) => {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/crm/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ name: newName.trim(), price: parseFloat(newPrice) || null }),
      });
      if (r.ok) {
        setNewName(''); setNewPrice(''); setAdding(false);
        onRefresh();
        toast.success('Service added');
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Failed to add service');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/crm/services/${id}`, { method: 'DELETE', headers: getAuthHeader() });
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end justify-center p-4">
      <div className="bg-[#111113] rounded-2xl border border-white/10 p-5 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Services & Products
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
          {services.map(s => (
            <div key={s.id} className="flex items-center gap-3 bg-zinc-900/60 rounded-xl px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{s.name}</p>
                {s.price && (
                  <p className="text-zinc-500 text-xs font-mono">{formatCurrencyFull(s.price)}</p>
                )}
              </div>
              <button onClick={() => handleDelete(s.id)} className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {services.length === 0 && (
            <p className="text-zinc-600 text-sm text-center py-4">No services yet</p>
          )}
        </div>

        {adding ? (
          <div className="space-y-2">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Service name *"
              className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#22c55e]/50"
              autoFocus
            />
            <input
              type="number"
              value={newPrice}
              onChange={e => setNewPrice(e.target.value)}
              placeholder="Default price (₹) — optional"
              className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-2 text-sm text-white font-mono placeholder:text-zinc-600 focus:outline-none focus:border-[#22c55e]/50"
            />
            <div className="flex gap-2">
              <button onClick={() => { setAdding(false); setNewName(''); setNewPrice(''); }}
                className="flex-1 py-2 rounded-xl border border-white/10 text-zinc-400 text-sm">
                Cancel
              </button>
              <button onClick={handleAdd} disabled={saving || !newName.trim()}
                className="flex-1 py-2 rounded-xl bg-[#22c55e] text-white text-sm font-bold disabled:opacity-50">
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="w-full py-2.5 rounded-xl border border-dashed border-white/20 text-zinc-400 text-sm hover:border-[#22c55e]/40 hover:text-[#22c55e] transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Service
          </button>
        )}
      </div>
    </div>
  );
};

// ── Conversion Modal ──────────────────────────────────────────────────────────

const ConversionModal = ({ lead, services, onSave, onClose, onOpenServiceManager }) => {
  const [selectedService, setSelectedService] = useState('');
  const [customService, setCustomService] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const svcName = selectedService === 'custom' ? customService.trim() : selectedService;
    setSaving(true);
    await onSave(lead, svcName, parseFloat(value) || 0);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#111113] rounded-2xl border border-white/10 p-6 w-full max-w-sm">
        <h3 className="font-bold text-white text-lg mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Log Conversion
        </h3>
        <p className="text-zinc-500 text-sm mb-4 font-mono">{lead.name}</p>

        <label className="text-xs text-zinc-500 block mb-2 uppercase tracking-wider font-semibold">
          Service / Product Sold
        </label>
        {services.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 mb-3">
            {services.map(s => (
              <button
                key={s.id}
                onClick={() => { setSelectedService(s.name); if (s.price) setValue(String(s.price)); }}
                className={`p-2.5 rounded-xl border text-sm text-left transition-colors ${
                  selectedService === s.name
                    ? 'border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e]'
                    : 'border-white/10 text-zinc-300 hover:border-white/20'
                }`}
              >
                <p className="font-semibold text-xs truncate">{s.name}</p>
                {s.price && <p className="text-[10px] text-zinc-500 font-mono">{formatCurrencyFull(s.price)}</p>}
              </button>
            ))}
            <button
              onClick={() => setSelectedService('custom')}
              className={`p-2.5 rounded-xl border text-sm transition-colors ${
                selectedService === 'custom'
                  ? 'border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e]'
                  : 'border-dashed border-white/20 text-zinc-500 hover:border-white/30'
              }`}
            >
              + Other
            </button>
          </div>
        ) : (
          <p className="text-xs text-zinc-600 mb-3">
            No services configured.{' '}
            <button onClick={onOpenServiceManager} className="text-[#6366f1] underline">
              Add services
            </button>
          </p>
        )}

        {selectedService === 'custom' && (
          <input
            value={customService}
            onChange={e => setCustomService(e.target.value)}
            placeholder="Service name..."
            className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:border-[#22c55e]/50"
            autoFocus
          />
        )}

        <label className="text-xs text-zinc-500 block mb-2 uppercase tracking-wider font-semibold">
          Conversion Value (₹)
        </label>
        <div className="relative mb-4">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">₹</span>
          <input
            type="number"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="0"
            className="w-full bg-zinc-900 border border-white/10 rounded-xl pl-7 pr-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-[#22c55e]/50"
          />
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (!selectedService || (selectedService === 'custom' && !customService.trim()))}
            className="flex-1 py-2.5 rounded-xl bg-[#22c55e] text-white font-bold text-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Log Win'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Lost Modal ────────────────────────────────────────────────────────────────

const LostModal = ({ lead, onSave, onClose }) => {
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    await onSave(lead, selected);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#111113] rounded-2xl border border-white/10 p-5 w-full max-w-sm">
        <h3 className="font-bold text-white text-base mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Mark as Lost
        </h3>
        <p className="text-zinc-500 text-sm mb-4 font-mono">{lead.name}</p>

        <label className="text-xs text-zinc-500 block mb-3 uppercase tracking-wider font-semibold">
          Reason
        </label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {LOST_SUBSTATUS.map(s => (
            <button
              key={s.value}
              onClick={() => setSelected(s.value)}
              className={`p-2.5 rounded-xl border text-left transition-colors ${
                selected === s.value ? s.colorClass : 'border-white/10 text-zinc-400 hover:border-white/20'
              }`}
            >
              <p className="font-semibold text-xs">{s.label}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{s.desc}</p>
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selected}
            className="flex-1 py-2.5 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30 font-bold text-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Mark Lost'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Stats Row ─────────────────────────────────────────────────────────────────

const StatsRow = ({ stats, leads, loading, onFilterClick }) => {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="animate-pulse bg-zinc-800/40 rounded-2xl h-20" />
        ))}
      </div>
    );
  }

  const convRate = parseFloat(stats.conversion_rate) || 0;
  const today = new Date().toISOString().slice(0, 10);
  const urgentLeads = leads.filter(l => l.sla_status?.severity === 'critical' && l.status === 'new').length;
  const overdueFollowups = leads.filter(l => l.status === 'follow_up' && l.follow_up_at && new Date(l.follow_up_at) < new Date()).length;
  const todayWon = leads.filter(l => l.status === 'converted' && l.updated_at?.startsWith(today)).length;
  const totalRevenue = leads.filter(l => l.actual_value).reduce((s, l) => s + (l.actual_value || 0), 0);

  const cards = [
    {
      label: 'Urgent',
      value: urgentLeads,
      sub: 'Need immediate call',
      color: urgentLeads > 0 ? 'text-red-400' : 'text-zinc-500',
      iconBg: urgentLeads > 0 ? 'bg-red-500/10' : 'bg-white/5',
      Icon: AlertTriangle,
      filter: 'urgent',
    },
    {
      label: 'Overdue F/U',
      value: overdueFollowups,
      sub: overdueFollowups > 0 ? 'Needs attention' : 'All clear',
      color: overdueFollowups > 0 ? 'text-amber-400' : 'text-zinc-500',
      iconBg: overdueFollowups > 0 ? 'bg-amber-500/10' : 'bg-white/5',
      Icon: Clock,
      filter: 'overdue_fu',
    },
    {
      label: 'Won Today',
      value: todayWon,
      sub: null,
      color: 'text-[#22c55e]',
      iconBg: 'bg-[#22c55e]/10',
      Icon: CheckCircle2,
      filter: null,
    },
    {
      label: 'Revenue',
      value: totalRevenue > 0 ? formatCurrency(totalRevenue) : '—',
      sub: null,
      color: 'text-[#22c55e]',
      iconBg: 'bg-[#22c55e]/10',
      Icon: DollarSign,
      filter: null,
    },
    {
      label: 'Conversion',
      value: `${convRate}%`,
      sub: null,
      color: 'text-[#6366f1]',
      iconBg: 'bg-[#6366f1]/10',
      Icon: TrendingUp,
      extra: (
        <div className="mt-2 h-1 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-[#6366f1] rounded-full" style={{ width: `${Math.min(convRate, 100)}%` }} />
        </div>
      ),
      filter: null,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
      {cards.map(({ Icon, label, value, sub, color, iconBg, extra, filter }) => (
        <div
          key={label}
          onClick={() => filter && onFilterClick && onFilterClick(filter)}
          className={`bg-[#09090B] border border-white/5 rounded-2xl p-4 ${filter ? 'cursor-pointer hover:border-white/10 transition-colors' : ''}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg}`}>
              <Icon className={`w-3.5 h-3.5 ${color}`} />
            </div>
            <span className="text-xs text-zinc-500">{label}</span>
          </div>
          <p className={`text-xl font-bold font-mono ${color}`}>{value}</p>
          {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
          {extra}
        </div>
      ))}
    </div>
  );
};

// ── Add Lead Modal ─────────────────────────────────────────────────────────────

const AddLeadModal = ({ onClose, onSave, readOnly, customFieldDefs = [] }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', source: 'manual', notes: '', potential_value: '', custom_data: {} });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Name and phone are required'); return; }
    setSaving(true);
    const payload = { ...form, potential_value: form.potential_value ? parseFloat(form.potential_value) : undefined };
    if (!payload.custom_data || Object.keys(payload.custom_data).length === 0) delete payload.custom_data;
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) setForm({ name: '', phone: '', email: '', company: '', source: 'manual', notes: '', potential_value: '', custom_data: {} });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-[#09090B] rounded-t-3xl sm:rounded-2xl border border-white/10 shadow-2xl w-full sm:max-w-md relative z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <h3 className="font-bold text-white">Add New Lead</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {[
            { k: 'name', label: 'Full Name *', placeholder: 'Rajesh Kumar', type: 'text' },
            { k: 'phone', label: 'Phone *', placeholder: '+91 98765 43210', type: 'tel', pattern: '[0-9+\\-\\s()]*' },
            { k: 'email', label: 'Email', placeholder: 'email@example.com', type: 'email' },
            { k: 'company', label: 'Company', placeholder: 'Company name', type: 'text' },
            { k: 'potential_value', label: 'Potential Value (₹)', placeholder: '50000', type: 'number', min: '0' },
          ].map(({ k, label, placeholder, type, pattern, min }) => (
            <div key={k}>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">{label}</label>
              <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder}
                {...(pattern ? { pattern } : {})}
                {...(min !== undefined ? { min } : {})}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Source</label>
            <select value={form.source} onChange={e => set('source', e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30">
              {['manual', 'meta', 'google', 'referral', 'walk_in'].map(s => (
                <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything worth noting..." rows={2}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 resize-none"
            />
          </div>
          {customFieldDefs.map(def => (
            <div key={def.field_key}>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">{def.field_label}</label>
              <input
                type={def.field_type === 'number' ? 'number' : 'text'}
                value={form.custom_data?.[def.field_key] || ''}
                onChange={e => set('custom_data', { ...form.custom_data, [def.field_key]: e.target.value })}
                className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#6366f1]/50"
                placeholder={`Enter ${def.field_label.toLowerCase()}`}
              />
            </div>
          ))}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-zinc-400 text-sm font-medium hover:bg-white/10">Cancel</button>
            <button onClick={handleSave} disabled={saving || readOnly}
              className="flex-1 py-2.5 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 disabled:opacity-50">
              {saving ? 'Saving…' : 'Add Lead'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Call Outcome Drawer ───────────────────────────────────────────────────────

const CallOutcomeDrawer = ({ lead, onClose, onSave, readOnly }) => {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [reasonForLoss, setReasonForLoss] = useState('');
  const [saving, setSaving] = useState(false);

  const showLossReason = outcome === 'not_interested';

  const handleSave = async () => {
    if (!outcome) { toast.error('Select an outcome'); return; }
    setSaving(true);
    await onSave(lead.id, { lead_id: lead.id, outcome, notes, duration: 0, reason_for_loss: reasonForLoss || undefined });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-end">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-[#09090B] rounded-t-3xl md:rounded-none md:rounded-l-2xl border-t md:border-l md:border-y border-white/10 shadow-2xl w-full md:w-96 md:h-full relative z-10 flex flex-col max-h-[90vh] md:max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#22c55e]/10 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-[#22c55e]" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">Log Call</h3>
              <p className="text-xs text-zinc-500 font-mono">{lead.name} · {lead.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 flex-1">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Call Outcome</p>
          <div className="grid grid-cols-2 gap-2">
            {CALL_OUTCOMES.map(o => (
              <button key={o.id} onClick={() => setOutcome(o.id)}
                className={`text-left px-3 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                  outcome === o.id
                    ? `${o.color} ${o.border} ${o.bg}`
                    : 'text-zinc-400 border-white/5 hover:border-white/15 bg-black/20'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {showLossReason && (
            <div>
              <label className="block text-xs font-semibold text-red-400 uppercase tracking-wider mb-1.5">Reason for Loss</label>
              <input
                value={reasonForLoss}
                onChange={e => setReasonForLoss(e.target.value)}
                placeholder="e.g. Price too high, Not the right fit..."
                className="w-full bg-red-500/5 border border-red-500/20 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-500/40"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)..." rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 resize-none"
            />
          </div>
        </div>

        <div className="p-5 border-t border-white/10 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-zinc-400 text-sm font-medium">Cancel</button>
          <button onClick={handleSave} disabled={saving || !outcome || readOnly}
            className="flex-1 py-2.5 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 disabled:opacity-50">
            {saving ? 'Saving…' : 'Log Call'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Lead Detail Drawer ─────────────────────────────────────────────────────────

const PIPELINE_STEPS = ['new', 'called', 'follow_up', 'converted'];

const StatusPipeline = ({ current }) => {
  const idx = PIPELINE_STEPS.indexOf(current);
  const isLost = current === 'lost';
  return (
    <div className="flex items-center gap-0 mt-2">
      {PIPELINE_STEPS.map((step, i) => {
        const sc = STATUS_CONFIG[step];
        const done = isLost ? false : i <= idx;
        const active = !isLost && i === idx;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border ${
                active ? `${sc.bg} ${sc.border} ${sc.text}`
                : done ? 'bg-[#22c55e]/20 border-[#22c55e]/30 text-[#22c55e]'
                : 'bg-transparent border-zinc-800 text-zinc-700'
              }`}>
                {done && !active ? <Check className="w-2.5 h-2.5" /> : i + 1}
              </div>
              <span className={`text-[8px] mt-0.5 font-semibold ${active ? sc.text : done ? 'text-[#22c55e]' : 'text-zinc-700'}`}>
                {sc.label}
              </span>
            </div>
            {i < PIPELINE_STEPS.length - 1 && (
              <div className={`flex-1 h-px mb-3.5 ${done && !active ? 'bg-[#22c55e]/40' : 'bg-zinc-800'}`} />
            )}
          </React.Fragment>
        );
      })}
      {isLost && (
        <div className="ml-2 px-2 py-0.5 rounded bg-red-500/20 border border-red-500/30 text-[9px] font-bold text-red-400">LOST</div>
      )}
    </div>
  );
};

const LeadDetailDrawer = ({ lead, calls, onClose, onStatusChange, readOnly, customFieldDefs = [] }) => {
  const sc = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  const rc = lead.rating ? RATING_CONFIG[lead.rating] : null;
  const RatingIcon = rc?.icon;
  const hint = getSmartHint(lead);

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-[#09090B] rounded-t-3xl sm:rounded-2xl border border-white/10 shadow-2xl w-full sm:max-w-lg relative z-10 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white border-2 ${sc.border} bg-white/5`}>
              {lead.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white">{lead.name}</h3>
                {RatingIcon && <RatingIcon className={`w-4 h-4 ${rc.color}`} />}
              </div>
              <p className="text-xs text-zinc-500 font-mono">{lead.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Smart hint banner */}
          {hint && (
            <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs font-medium ${HINT_COLORS[hint.color]}`}>
              <hint.Icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{hint.text}</span>
            </div>
          )}

          {/* Pipeline */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Progress</p>
            <StatusPipeline current={lead.status} />
          </div>

          {/* Status + meta badges */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${sc.bg} ${sc.text}`}>{sc.label}</span>
            {lead.sub_status && <SubStatusBadge subStatus={lead.sub_status} />}
            {lead.sla_status && <SLABadge sla={lead.sla_status} />}
            {lead.potential_value && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-[#22c55e]/10 text-[#22c55e]">
                {formatCurrency(lead.potential_value)} potential
              </span>
            )}
          </div>

          {/* Won conversion info */}
          {lead.status === 'converted' && lead.conversion_service && (
            <div className="flex items-center gap-2 bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-xl px-3 py-2.5">
              <CheckCircle2 className="w-4 h-4 text-[#22c55e] flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-[#22c55e]">{lead.conversion_service}</p>
                {lead.actual_value && (
                  <p className="text-xs text-[#22c55e] font-mono">{formatCurrencyFull(lead.actual_value)}</p>
                )}
              </div>
            </div>
          )}

          {/* Status change */}
          {!readOnly && (
            <div>
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Update Status</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <button key={k} onClick={() => onStatusChange(lead, k)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      lead.status === k ? `${v.bg} ${v.text} ring-1 ring-current` : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                    }`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contact actions */}
          <div className="flex gap-2">
            <a href={`tel:${lead.phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 transition-colors">
              <Phone className="w-4 h-4" />Call
            </a>
            <a href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/20 text-sm font-bold hover:bg-[#25D366]/20 transition-colors">
              <MessageCircle className="w-4 h-4" />WhatsApp
            </a>
          </div>

          {/* Details */}
          <div className="bg-black/20 rounded-2xl p-4 space-y-2 text-sm">
            {lead.company && <div className="flex justify-between"><span className="text-zinc-500">Company</span><span className="text-white">{lead.company}</span></div>}
            {lead.email && <div className="flex justify-between"><span className="text-zinc-500">Email</span><span className="text-white font-mono text-xs">{lead.email}</span></div>}
            <div className="flex justify-between items-center"><span className="text-zinc-500">Source</span><SourceBadge source={lead.source} /></div>
            <div className="flex justify-between"><span className="text-zinc-500">Calls made</span><span className="text-white font-mono">{lead.call_count}</span></div>
            {lead.actual_value && (
              <div className="flex justify-between">
                <span className="text-zinc-500">Closed value</span>
                <span className="text-[#22c55e] font-mono font-bold">{formatCurrency(lead.actual_value)}</span>
              </div>
            )}
            {lead.campaign_name && (
              <div className="flex justify-between gap-3">
                <span className="text-zinc-500 flex-shrink-0">Campaign</span>
                <span className="text-white text-xs text-right">{lead.campaign_name}</span>
              </div>
            )}
            {lead.ad_name && (
              <div className="flex justify-between gap-3">
                <span className="text-zinc-500 flex-shrink-0">Ad</span>
                <span className="text-zinc-400 text-xs text-right">{lead.ad_name}</span>
              </div>
            )}
            {lead.notes && (
              <div>
                <span className="text-zinc-500 block mb-1">Notes</span>
                <p className="text-zinc-300 text-xs leading-relaxed">{lead.notes}</p>
              </div>
            )}
            {(lead.reason_for_loss || lead.loss_reason) && (
              <div className="mt-1 pt-2 border-t border-red-500/20">
                <span className="text-red-400 text-xs font-semibold block mb-1">Reason for Loss</span>
                <p className="text-zinc-300 text-xs">{lead.reason_for_loss || lead.loss_reason}</p>
              </div>
            )}
          </div>

          {/* Custom Fields (Form Responses) */}
          {lead.custom_data && Object.keys(lead.custom_data).length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-2">Form Responses</p>
              {Object.entries(lead.custom_data).map(([key, val]) => {
                if (!val) return null;
                const def = customFieldDefs.find(d => d.field_key === key);
                const label = def?.field_label || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                return (
                  <div key={key} className="flex justify-between gap-3 py-1">
                    <span className="text-zinc-500 text-xs flex-shrink-0">{label}</span>
                    <span className="text-white text-xs text-right font-mono">{val}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Call history */}
          {calls.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Call Timeline</p>
              <div className="relative pl-4">
                <div className="absolute left-1.5 top-0 bottom-0 w-px bg-white/10" />
                <div className="space-y-3">
                  {calls.map((c) => (
                    <div key={c.id} className="relative">
                      <div className="absolute -left-[11px] w-2.5 h-2.5 rounded-full bg-[#22c55e] border-2 border-[#09090B] top-1" />
                      <div className="bg-black/20 rounded-xl px-3 py-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-white capitalize">{c.outcome.replace('_', ' ')}</p>
                          <p className="text-[10px] text-zinc-600">{timeAgo(c.created_at)}</p>
                        </div>
                        {c.notes && <p className="text-[10px] text-zinc-500 mt-0.5">{c.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── CSV Bulk Import Modal ─────────────────────────────────────────────────────

const BulkImportModal = ({ onClose, onImport }) => {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const csv = 'name,phone,email,campaign_name\nRajesh Kumar,+919876543210,rajesh@example.com,Summer Campaign';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'leads_template.csv';
    a.click();
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) { toast.error('CSV must have a header row and at least one data row'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIdx = headers.indexOf('name');
    const phoneIdx = headers.indexOf('phone');
    const emailIdx = headers.indexOf('email');
    const campaignIdx = headers.indexOf('campaign_name');
    if (nameIdx === -1 || phoneIdx === -1) { toast.error('CSV must have "name" and "phone" columns'); return; }
    const parsed = lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      return {
        name: cols[nameIdx] || '',
        phone: cols[phoneIdx] || '',
        email: emailIdx !== -1 ? cols[emailIdx] : '',
        campaign_name: campaignIdx !== -1 ? cols[campaignIdx] : '',
        source: 'bulk_upload',
      };
    }).filter(r => r.name && r.phone);
    setRows(parsed);
    setPreview(parsed.slice(0, 5));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => parseCSV(ev.target.result);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setImporting(true);
    const res = await onImport(rows);
    setResult(res);
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-[#09090B] rounded-t-3xl sm:rounded-2xl border border-white/10 shadow-2xl w-full sm:max-w-lg relative z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <Upload className="w-4 h-4 text-indigo-400" />
            </div>
            <h3 className="font-bold text-white">Import CSV</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {result ? (
            <div className="space-y-4">
              <div className="bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold font-mono text-[#22c55e]">{result.inserted}</p>
                <p className="text-sm text-zinc-400 mt-1">Leads imported</p>
              </div>
              {result.skipped > 0 && (
                <p className="text-center text-sm text-zinc-500">{result.skipped} duplicate{result.skipped !== 1 ? 's' : ''} skipped</p>
              )}
              {result.errors?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                  <p className="text-xs text-red-400 font-semibold mb-1">{result.errors.length} errors:</p>
                  {result.errors.slice(0, 3).map((e, i) => <p key={i} className="text-xs text-zinc-500">{e}</p>)}
                </div>
              )}
              <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-medium hover:bg-white/10">
                Done
              </button>
            </div>
          ) : (
            <>
              <button onClick={downloadTemplate}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 border border-white/10 text-zinc-300 text-sm font-medium hover:bg-white/10">
                <Download className="w-4 h-4" />Download CSV Template
              </button>

              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-colors"
              >
                <FileText className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Click to select CSV file</p>
                <p className="text-xs text-zinc-600 mt-1">Columns: name, phone, email, campaign_name</p>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
              </div>

              {preview.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                    Preview ({rows.length} rows)
                  </p>
                  <div className="overflow-x-auto rounded-xl border border-white/10">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/10">
                          {['Name', 'Phone', 'Email', 'Campaign'].map(h => (
                            <th key={h} className="px-3 py-2 text-left text-zinc-500 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((r, i) => (
                          <tr key={i} className="border-b border-white/5 last:border-0">
                            <td className="px-3 py-2 text-white">{r.name}</td>
                            <td className="px-3 py-2 text-zinc-400 font-mono">{r.phone}</td>
                            <td className="px-3 py-2 text-zinc-500">{r.email || '—'}</td>
                            <td className="px-3 py-2 text-zinc-500">{r.campaign_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 5 && (
                      <p className="text-center text-[10px] text-zinc-600 py-2">+{rows.length - 5} more rows</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-zinc-400 text-sm font-medium">Cancel</button>
                <button
                  onClick={handleImport}
                  disabled={importing || rows.length === 0}
                  className="flex-1 py-2.5 rounded-xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-sm font-bold hover:bg-indigo-500/30 disabled:opacity-50"
                >
                  {importing ? 'Importing…' : `Upload ${rows.length} Lead${rows.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Kanban Column ─────────────────────────────────────────────────────────────

const KanbanColumn = ({ statusKey, leads, onLeadClick, onCallClick }) => {
  const sc = STATUS_CONFIG[statusKey];
  const totalValue = leads.reduce((sum, l) => sum + (l.potential_value || 0), 0);

  return (
    <div className="flex-shrink-0 w-72">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl mb-3 ${sc.kanbanBg} border ${sc.border}`}>
        <span className={`text-xs font-bold ${sc.text}`}>{sc.label}</span>
        <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${sc.bg} ${sc.text}`}>{leads.length}</span>
        {totalValue > 0 && (
          <span className="text-[10px] text-zinc-500 font-mono">{formatCurrency(totalValue)}</span>
        )}
      </div>

      <div className="space-y-2 min-h-24">
        {leads.map(lead => {
          const rc = lead.rating ? RATING_CONFIG[lead.rating] : null;
          const RIcon = rc?.icon;
          return (
            <div
              key={lead.id}
              onClick={() => onLeadClick(lead)}
              className="bg-[#09090B] border border-white/5 rounded-xl p-3 cursor-pointer hover:border-white/15 transition-colors active:scale-[0.99] group"
            >
              <div className="flex items-start gap-2 mb-2">
                <div className="relative w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                  {lead.name.charAt(0).toUpperCase()}
                  {RIcon && (
                    <span className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#09090B] flex items-center justify-center ${rc.color}`}>
                      <RIcon className="w-2 h-2" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-xs truncate">{lead.name}</p>
                  <p className="text-zinc-500 font-mono text-[10px] truncate">{lead.phone}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                <SourceBadge source={lead.source} />
                {lead.sub_status && <SubStatusBadge subStatus={lead.sub_status} />}
                {lead.sla_status && <SLABadge sla={lead.sla_status} />}
                {lead.potential_value && (
                  <span className="text-[10px] text-[#22c55e] font-mono font-bold">{formatCurrency(lead.potential_value)}</span>
                )}
              </div>

              {lead.status === 'converted' && lead.conversion_service && (
                <div className="flex items-center gap-1 mt-1.5">
                  <CheckCircle2 className="w-3 h-3 text-[#22c55e]" />
                  <span className="text-[10px] text-[#22c55e] font-semibold truncate">{lead.conversion_service}</span>
                  {lead.actual_value && <span className="text-[10px] text-[#22c55e] font-mono">· {formatCurrency(lead.actual_value)}</span>}
                </div>
              )}

              {lead.campaign_name && (
                <p className="text-[10px] text-zinc-600 mt-1.5 truncate">{lead.campaign_name}</p>
              )}

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                <span className="text-[10px] text-zinc-600">{lead.call_count} calls</span>
                <button
                  onClick={e => { e.stopPropagation(); onCallClick(lead); }}
                  className="w-6 h-6 rounded-lg bg-[#22c55e]/10 flex items-center justify-center text-[#22c55e] hover:bg-[#22c55e]/20 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Phone className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}

        {leads.length === 0 && (
          <div className="border border-dashed border-white/5 rounded-xl p-4 text-center">
            <p className="text-[10px] text-zinc-700">No leads</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Kanban Board ──────────────────────────────────────────────────────────────

const KanbanBoard = ({ leads, onLeadClick, onCallClick }) => {
  const columns = Object.keys(STATUS_CONFIG);
  const byStatus = columns.reduce((acc, key) => {
    acc[key] = leads.filter(l => l.status === key);
    return acc;
  }, {});

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-none">
      {columns.map(key => (
        <KanbanColumn
          key={key}
          statusKey={key}
          leads={byStatus[key] || []}
          onLeadClick={onLeadClick}
          onCallClick={onCallClick}
        />
      ))}
    </div>
  );
};

// ── Empty State ───────────────────────────────────────────────────────────────

const EmptyState = ({ readOnly, onAdd, filter }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
      <Users className="w-8 h-8 text-zinc-600" />
    </div>
    <p className="text-zinc-400 font-semibold">
      {filter !== 'all' ? `No ${STATUS_CONFIG[filter]?.label || filter} leads` : 'No leads yet'}
    </p>
    {!readOnly && filter === 'all' && (
      <div className="mt-4 space-y-2">
        <button onClick={onAdd}
          className="block mx-auto px-5 py-2.5 rounded-xl bg-[#22c55e]/10 text-[#22c55e] text-sm font-bold border border-[#22c55e]/20 hover:bg-[#22c55e]/20 transition-colors">
          Add your first lead
        </button>
        <p className="text-xs text-zinc-600">
          Or link a Meta form in{' '}
          <span className="text-[#6366f1]">Portal Access Control → Lead Form Setup</span>{' '}
          to auto-import leads
        </p>
      </div>
    )}
    {readOnly && <p className="text-zinc-600 text-sm mt-1">No leads have been added yet</p>}
  </div>
);

// ── Main CRM View ─────────────────────────────────────────────────────────────

export default function CRMView({ readOnly = false }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [slaFilter, setSlaFilter] = useState('all');
  const [activeView, setActiveView] = useState('leads');
  const [displayMode, setDisplayMode] = useState('list');
  const [showAddLead, setShowAddLead] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showServiceManager, setShowServiceManager] = useState(false);
  const [callDrawer, setCallDrawer] = useState(null);
  const [detailDrawer, setDetailDrawer] = useState(null);
  const [detailCalls, setDetailCalls] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [conversionModal, setConversionModal] = useState(null);
  const [lostModal, setLostModal] = useState(null);

  const headers = { 'Content-Type': 'application/json', ...getAuthHeader() };

  const buildLeadsUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('status', filter);
    if (search) params.set('search', search);
    return `${API_URL}/crm/leads?${params.toString()}`;
  }, [filter, search]);

  const fetchServices = useCallback(() => {
    fetch(`${API_URL}/crm/services`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => setServices(d.services || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setNetworkError(false);
    try {
      const [leadsRes, statsRes, fuRes] = await Promise.all([
        fetch(buildLeadsUrl(), { headers: getAuthHeader() }),
        fetch(`${API_URL}/crm/stats`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/crm/followups`, { headers: getAuthHeader() }),
      ]);
      if (leadsRes.ok) { const d = await leadsRes.json(); setLeads(d.leads || []); }
      if (statsRes.ok) { setStats(await statsRes.json()); }
      if (fuRes.ok) { const d = await fuRes.json(); setFollowups(d.followups || []); }
    } catch {
      setNetworkError(true);
      toast.error('Failed to load CRM data');
    }
    setLoading(false);
  }, [buildLeadsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    fetch(`${API_URL}/crm/custom-fields`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => setCustomFieldDefs(d.fields || []))
      .catch(() => {});
    fetchServices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (lead) => {
    setDetailDrawer(lead);
    try {
      const r = await fetch(`${API_URL}/crm/calls/${lead.id}`, { headers: getAuthHeader() });
      if (r.ok) { const d = await r.json(); setDetailCalls(d.calls || []); }
    } catch { setDetailCalls([]); }
  };

  const handleAddLead = async (form) => {
    const res = await fetch(`${API_URL}/crm/leads`, { method: 'POST', headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success('Lead added'); setShowAddLead(false); fetchAll(); return true; }
    else { const e = await res.json(); toast.error(e.detail || 'Failed to add lead'); return false; }
  };

  const handleLogCall = async (leadId, body) => {
    const res = await fetch(`${API_URL}/crm/calls`, { method: 'POST', headers, body: JSON.stringify(body) });
    if (res.ok) { toast.success('Call logged'); setCallDrawer(null); fetchAll(); }
    else { const e = await res.json(); toast.error(e.detail || 'Failed to log call'); }
  };

  const updateLead = async (leadId, updates) => {
    const res = await fetch(`${API_URL}/crm/leads/${leadId}`, {
      method: 'PUT', headers, body: JSON.stringify(updates),
    });
    if (res.ok) {
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...updates } : l));
      setDetailDrawer(prev => prev?.id === leadId ? { ...prev, ...updates } : prev);
      return true;
    } else {
      const e = await res.json();
      toast.error(e.detail || 'Update failed');
      return false;
    }
  };

  const handleStatusChange = async (lead, newStatus) => {
    if (newStatus === 'lost') {
      setLostModal(lead);
      return;
    }
    if (newStatus === 'converted') {
      setConversionModal(lead);
      return;
    }
    const ok = await updateLead(lead.id, { status: newStatus, sub_status: null });
    if (ok) toast.success('Status updated');
  };

  const handleLostSubmit = async (lead, subStatus) => {
    const ok = await updateLead(lead.id, { status: 'lost', sub_status: subStatus });
    if (ok) {
      toast.success('Marked as lost');
      setLostModal(null);
      setDetailDrawer(null);
    }
  };

  const handleConversionSubmit = async (lead, service, value) => {
    const ok = await updateLead(lead.id, {
      status: 'converted',
      conversion_service: service || null,
      actual_value: value || null,
      sub_status: null,
    });
    if (ok) {
      toast.success(`Conversion logged${value ? ` — ${formatCurrencyFull(value)}` : ''}`);
      setConversionModal(null);
      setDetailDrawer(null);
      fetchAll();
    }
  };

  const handleBulkImport = async (rows) => {
    try {
      const res = await fetch(`${API_URL}/crm/leads/bulk`, {
        method: 'POST', headers,
        body: JSON.stringify({ leads: rows }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.inserted} leads imported${d.skipped ? `, ${d.skipped} skipped` : ''}`);
        fetchAll();
        return d;
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Import failed');
        return null;
      }
    } catch {
      toast.error('Network error during import');
      return null;
    }
  };

  const handleCompleteFollowup = async (fuId) => {
    const res = await fetch(`${API_URL}/crm/followups/${fuId}/complete`, { method: 'PUT', headers });
    if (res.ok) { toast.success('Follow-up marked done'); fetchAll(); }
  };

  const handleStatFilterClick = (filterType) => {
    if (filterType === 'urgent') {
      setSlaFilter('overdue');
      setFilter('new');
      setActiveView('leads');
    } else if (filterType === 'overdue_fu') {
      setActiveView('followups');
    }
  };

  // Client-side source/sla filters applied on top of server-side status filter
  const filteredLeads = leads.filter(lead => {
    if (sourceFilter !== 'all' && (lead.source || 'manual') !== sourceFilter) return false;
    if (slaFilter === 'overdue' && (!lead.sla_status || lead.sla_status.severity !== 'critical')) return false;
    if (slaFilter === 'due_today' && (!lead.sla_status || lead.sla_status.severity === 'critical')) return false;
    if (slaFilter === 'on_track' && lead.sla_status) return false;
    return true;
  });

  const overdueCount = followups.filter(f => f.is_overdue).length;

  // Monthly revenue for header display
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthRevenue = leads
    .filter(l => l.status === 'converted' && l.actual_value && l.updated_at?.startsWith(thisMonth))
    .reduce((s, l) => s + (l.actual_value || 0), 0);

  const statusFilters = [
    { id: 'all', label: 'All' },
    { id: 'new', label: 'New' },
    { id: 'called', label: 'Called' },
    { id: 'follow_up', label: 'Callbacks' },
    { id: 'converted', label: 'Won' },
    { id: 'lost', label: 'Lost' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Stats */}
      <StatsRow stats={stats} leads={leads} loading={loading} onFilterClick={handleStatFilterClick} />

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {/* View toggle: leads/followups */}
        <div className="flex bg-black/40 border border-white/10 rounded-xl p-1 gap-1">
          <button onClick={() => setActiveView('leads')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${activeView === 'leads' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'text-zinc-500 hover:text-white'}`}>
            <Users className="w-3.5 h-3.5 inline mr-1" />Leads
          </button>
          <button onClick={() => setActiveView('followups')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors relative ${activeView === 'followups' ? 'bg-orange-500/20 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>
            <Clock className="w-3.5 h-3.5 inline mr-1" />Follow-ups
            {overdueCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                {overdueCount}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-32">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..."
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
          />
        </div>

        {/* Display mode toggle (leads view only) */}
        {activeView === 'leads' && (
          <div className="flex bg-black/40 border border-white/10 rounded-xl p-1 gap-1">
            <button onClick={() => setDisplayMode('list')}
              title="List view"
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${displayMode === 'list' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'}`}>
              <List className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setDisplayMode('pipeline')}
              title="Pipeline view"
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${displayMode === 'pipeline' ? 'bg-[#6366f1]/20 text-[#6366f1]' : 'text-zinc-500 hover:text-white'}`}>
              <Columns className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Refresh */}
        <button onClick={fetchAll} className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>

        {/* Services button + revenue indicator */}
        {!readOnly && (
          <button
            onClick={() => setShowServiceManager(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-800/60 border border-white/10 text-zinc-400 text-xs font-bold hover:text-white hover:border-white/20 transition-colors"
            title="Manage Services"
          >
            <Settings className="w-3.5 h-3.5" />
            {monthRevenue > 0 ? (
              <span className="text-[#22c55e]">{formatCurrency(monthRevenue)}</span>
            ) : (
              <span>Services</span>
            )}
          </button>
        )}

        {/* Add/Import */}
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowBulkImport(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 text-xs font-bold hover:bg-indigo-500/20 transition-colors">
              <Upload className="w-3.5 h-3.5" />CSV
            </button>
            <button onClick={() => setShowAddLead(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 transition-colors">
              <Plus className="w-4 h-4" />Add Lead
            </button>
          </div>
        )}
      </div>

      {/* Status filter pills + extra filters */}
      {activeView === 'leads' && (
        <div className="mb-3 space-y-2">
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {statusFilters.map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                  filter === f.id ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'
                }`}>
                {f.label}
                {stats?.by_status && f.id !== 'all' && (
                  <span className="ml-1 opacity-60">{stats.by_status[f.id] || 0}</span>
                )}
              </button>
            ))}

            <button
              onClick={() => setShowFilters(v => !v)}
              className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ml-auto ${
                showFilters || sourceFilter !== 'all' || slaFilter !== 'all'
                  ? 'bg-[#6366f1]/20 text-[#6366f1]'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}>
              <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              Filters
              {(sourceFilter !== 'all' || slaFilter !== 'all') && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#6366f1] ml-0.5" />
              )}
            </button>
          </div>

          {showFilters && (
            <div className="flex gap-3 flex-wrap bg-black/20 border border-white/5 rounded-xl p-3">
              <div>
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Source</p>
                <div className="flex gap-1 flex-wrap">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'meta', label: 'Meta Ads' },
                    { id: 'meta_ads', label: 'Meta (Auto)' },
                    { id: 'manual', label: 'Manual' },
                    { id: 'bulk_upload', label: 'CSV Import' },
                  ].map(s => (
                    <button key={s.id} onClick={() => setSourceFilter(s.id)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors ${
                        sourceFilter === s.id ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30' : 'bg-white/5 text-zinc-500 hover:text-white border border-transparent'
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">SLA Status</p>
                <div className="flex gap-1 flex-wrap">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'overdue', label: 'Overdue' },
                    { id: 'due_today', label: 'Due Today' },
                    { id: 'on_track', label: 'On Track' },
                  ].map(s => (
                    <button key={s.id} onClick={() => setSlaFilter(s.id)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors ${
                        slaFilter === s.id ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-white/5 text-zinc-500 hover:text-white border border-transparent'
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {(sourceFilter !== 'all' || slaFilter !== 'all') && (
                <button
                  onClick={() => { setSourceFilter('all'); setSlaFilter('all'); }}
                  className="self-end px-2.5 py-1 rounded-lg text-[11px] font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Network error */}
      {networkError && !loading && (
        <div className="flex items-center justify-between bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3 mb-3">
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Connection failed. Check your network and try again.</span>
          </div>
          <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/20 text-red-400 text-xs font-bold hover:bg-red-500/30 flex-shrink-0">
            <RefreshCw className="w-3.5 h-3.5" />Retry
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-zinc-800/40 rounded-2xl h-20" />
            ))}
          </div>
        ) : activeView === 'leads' ? (
          filteredLeads.length === 0 ? (
            <EmptyState readOnly={readOnly} onAdd={() => setShowAddLead(true)} filter={filter} />
          ) : displayMode === 'pipeline' ? (
            <KanbanBoard leads={filteredLeads} onLeadClick={openDetail} onCallClick={setCallDrawer} />
          ) : (
            <div className="space-y-2">
              {filteredLeads.map((lead) => {
                const sc = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                const rc = lead.rating ? RATING_CONFIG[lead.rating] : null;
                const RIcon = rc?.icon;
                const isOverdueFollowup = lead.status === 'follow_up' && lead.follow_up_at && new Date(lead.follow_up_at) < new Date();
                return (
                  <div key={lead.id} onClick={() => openDetail(lead)}
                    className="bg-[#09090B] border border-white/5 rounded-2xl p-4 cursor-pointer hover:border-white/15 transition-colors active:scale-[0.99]">
                    <div className="flex items-start gap-3">
                      {/* Avatar with status ring */}
                      <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center font-bold text-white flex-shrink-0 bg-white/5 border-2 ${sc.border}`}>
                        {lead.name.charAt(0).toUpperCase()}
                        {RIcon && (
                          <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#09090B] flex items-center justify-center ${rc.color}`}>
                            <RIcon className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-white text-sm">{lead.name}</span>
                          {lead.sla_status && <SLABadge sla={lead.sla_status} />}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${sc.bg} ${sc.text}`}>{sc.label}</span>
                          {lead.sub_status && <SubStatusBadge subStatus={lead.sub_status} />}
                          <SourceBadge source={lead.source} />
                          {lead.company && <span className="text-[10px] text-zinc-500">{lead.company}</span>}
                          {lead.potential_value && <span className="text-[10px] text-[#22c55e] font-mono font-bold">{formatCurrency(lead.potential_value)}</span>}
                          {isOverdueFollowup && (
                            <span className="text-[10px] text-amber-400 font-mono font-semibold">overdue f/u</span>
                          )}
                        </div>
                        <p className="font-mono text-xs text-zinc-500 mt-1.5">{lead.phone}</p>
                        {lead.campaign_name && (
                          <p className="text-[10px] text-zinc-600 mt-0.5 truncate">{lead.campaign_name}</p>
                        )}
                        {/* Won: show conversion service */}
                        {lead.status === 'converted' && lead.conversion_service && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <CheckCircle2 className="w-3 h-3 text-[#22c55e]" />
                            <span className="text-xs text-[#22c55e] font-semibold">{lead.conversion_service}</span>
                            {lead.actual_value && (
                              <span className="text-xs text-[#22c55e] font-mono">· {formatCurrency(lead.actual_value)}</span>
                            )}
                          </div>
                        )}
                        {/* First custom field value if present */}
                        {customFieldDefs.length > 0 && lead.custom_data && Object.keys(lead.custom_data).length > 0 && (
                          Object.entries(lead.custom_data).slice(0, 1).map(([k, v]) => (
                            <span key={k} className="text-[10px] text-zinc-500 font-mono block mt-0.5">{v}</span>
                          ))
                        )}
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <a href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                          className="w-9 h-9 rounded-xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center hover:bg-[#25D366]/20 transition-colors">
                          <MessageCircle className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => { setCallDrawer(lead); window.location.href = `tel:${lead.phone}`; }}
                          className="w-10 h-10 rounded-xl bg-[#22c55e]/20 text-[#22c55e] flex items-center justify-center hover:bg-[#22c55e]/30 transition-colors">
                          <Phone className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {lead.last_activity && (
                      <div className="mt-2.5 pt-2.5 border-t border-white/5 flex items-center justify-between">
                        <span className="text-[10px] text-zinc-600">{lead.call_count} calls · {lead.last_activity}</span>
                        {lead.last_activity_at && <span className="text-[10px] text-zinc-600">{timeAgo(lead.last_activity_at)}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          // Follow-ups view
          followups.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No pending follow-ups</p>
            </div>
          ) : (
            <div className="space-y-2">
              {followups.map(fu => (
                <div key={fu.id} className={`bg-[#09090B] border rounded-2xl p-4 ${fu.is_overdue ? 'border-orange-500/30' : 'border-white/5'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${fu.is_overdue ? 'bg-orange-500/10 text-orange-400' : 'bg-white/5 text-zinc-400'}`}>
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white text-sm">{fu.lead_name}</span>
                        {fu.is_overdue && <span className="text-[10px] bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded font-bold">OVERDUE</span>}
                      </div>
                      <p className="text-xs text-zinc-500 font-mono">{fu.lead_phone}</p>
                      {fu.reason && <p className="text-[10px] text-zinc-500 mt-1 capitalize">{fu.reason.replace('_', ' ')}</p>}
                      <p className="text-[10px] text-zinc-600 mt-0.5">{new Date(fu.scheduled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</p>
                    </div>
                    {!readOnly && (
                      <button onClick={() => handleCompleteFollowup(fu.id)}
                        className="flex-shrink-0 px-3 py-1.5 rounded-xl bg-[#22c55e]/10 text-[#22c55e] text-xs font-bold border border-[#22c55e]/20 hover:bg-[#22c55e]/20 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Done
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Modals */}
      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onSave={handleAddLead}
          readOnly={readOnly}
          customFieldDefs={customFieldDefs}
        />
      )}
      {showBulkImport && (
        <BulkImportModal onClose={() => setShowBulkImport(false)} onImport={handleBulkImport} />
      )}
      {showServiceManager && (
        <ServiceManager
          services={services}
          onClose={() => setShowServiceManager(false)}
          onRefresh={fetchServices}
        />
      )}
      {callDrawer && (
        <CallOutcomeDrawer
          lead={callDrawer}
          onClose={() => setCallDrawer(null)}
          onSave={handleLogCall}
          readOnly={readOnly}
        />
      )}
      {detailDrawer && (
        <LeadDetailDrawer
          lead={detailDrawer}
          calls={detailCalls}
          onClose={() => { setDetailDrawer(null); setDetailCalls([]); }}
          onStatusChange={handleStatusChange}
          readOnly={readOnly}
          customFieldDefs={customFieldDefs}
        />
      )}
      {lostModal && (
        <LostModal
          lead={lostModal}
          onSave={handleLostSubmit}
          onClose={() => setLostModal(null)}
        />
      )}
      {conversionModal && (
        <ConversionModal
          lead={conversionModal}
          services={services}
          onSave={handleConversionSubmit}
          onClose={() => setConversionModal(null)}
          onOpenServiceManager={() => { setConversionModal(null); setShowServiceManager(true); }}
        />
      )}
    </div>
  );
}
